<template>
    <v-select
        v-model="selectedTags"
        :items="tags"
        :menu-props="{ maxHeight: '400' }"
        :placeholder="this.$vuetify.lang.t('$vuetify.employee.selectPermission')"
        filled dense clearable multiple persistent-hint
        background-color="#ffffff">
        <template v-slot:prepend-item>
            <v-list-item
                class="bg-white"
                @mousedown.prevent
                @click="onSelectAll">
                <v-list-item-action>
                    <v-checkbox
                        :input-value="selectedTags.length > 0"
                        :on-icon="headerActiveIcon['all']">
                    </v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                    <v-list-item-title>
                        {{ $vuetify.lang.t('$vuetify.employee.selectAll') }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
        </template>

        <template v-slot:item="{ item, attrs, on }">
            <v-list-item
                v-if="item.children"
                :class="parentClass(item.selected)"
                @mousedown.prevent
                @click="onSelectParent(item)">
                <!-- parent option -->
                <v-list-item-action>
                    <v-checkbox
                        :input-value="item.selected"
                        :on-icon="headerActiveIcon[item.value]">
                    </v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                    <v-list-item-title>
                        {{ item.text }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>

            <v-list-item
                v-else-if="item.parent"
                @mousedown.prevent
                @click="onSelectChild(item)"
                v-bind="attrs"
                #default="{ active }">
                <!-- child option -->
                <v-list-item-action style="margin-left: 32px;">
                    <v-checkbox :input-value="active"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                    <v-list-item-title>
                        {{ item.text }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>

            <v-list-item
                v-else
                v-on="on"
                v-bind="attrs"
                #default="{ active }">
                <!-- normal option -->
                <v-list-item-action>
                    <v-checkbox :input-value="active"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                    <v-list-item-title>
                        {{ item.text }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
        </template>
    </v-select>
</template>

<script>
export default {
    name: 'LinePermissionSelect',

    props: {
        value: {
            type: Array,
            default: null
        },
        tags: {
            type: Array,
            default: null
        }
    },

    data () {
        return {
            selectedTags: [],
            headerActiveIcon: {
                all: 'mdi-checkbox-marked'
            }
        }
    },

    methods: {
        parentClass (selected) {
            return selected ? 'v-list-item--active primary--text' : ''
        },

        getTagIndex (value) {
            return this.tags.findIndex(el => el.value === value)
        },

        addSelectedTags (values) {
            const { selectedTags } = this
            selectedTags.push(...values)
            this.selectedTags = [...new Set(selectedTags)]
        },

        removeSelectedTags (values) {
            let { selectedTags } = this
            selectedTags = selectedTags.filter(tag => !values.includes(tag))
            this.selectedTags = [...new Set(selectedTags)]
        },

        onSelectParent (item) {
            const { selected } = this.tags[this.getTagIndex(item.value)]

            this.tags[this.getTagIndex(item.value)].selected = !selected
            if (!selected) {
                this.headerActiveIcon[item.value] = 'mdi-checkbox-marked'
                this.addSelectedTags(item.children)
            } else {
                this.removeSelectedTags(item.children)
            }
        },

        onSelectChild (item) {
            const index = this.selectedTags.indexOf(item.value)
            const parent = this.tags[this.getTagIndex(item.parent)]
            if (index !== -1) {
                this.selectedTags.splice(index, 1)
                parent.selected = false
                for (let i = 0; i < this.selectedTags.length; i++) {
                    if (parent.children.includes(this.selectedTags[i])) {
                        this.headerActiveIcon[parent.value] = 'mdi-minus-box'
                        parent.selected = true
                        break
                    }
                }
            } else {
                this.addSelectedTags([item.value, parent.value])
                parent.selected = true

                let childSelected = 0
                for (let i = 0; (i < this.selectedTags.length) && (childSelected < parent.children.length); i++) {
                    if (parent.children.includes(this.selectedTags[i])) {
                        childSelected++
                    }
                }

                this.headerActiveIcon[parent.value] = (childSelected === parent.children.length) ? 'mdi-checkbox-marked' : 'mdi-minus-box'
            }

            this.tags[this.getTagIndex(item.parent)] = parent
        },

        onSelectAll () {
            if (this.selectedTags.length === 0) {
                const values = []
                this.tags.forEach(tag => {
                    if (tag.children) {
                        tag.selected = true
                    } else {
                        values.push(tag.value)
                    }

                    return tag
                })
                this.addSelectedTags(values)
            } else {
                this.selectedTags = []
                this.tags.forEach(tag => {
                    if (tag.children) {
                        tag.selected = false
                    }

                    return tag
                })
            }
        },

        addTagsHeaderIcon () {
            this.tags.forEach(tag => {
                if (tag.children) {
                    this.headerActiveIcon[tag.value] = 'mdi-checkbox-marked'
                }
            })
        },

        initSelected () {
            this.value.forEach(v => {
                const tag = this.tags.find(tag => tag.value === v)
                if (tag && tag.parent) {
                    this.onSelectChild(tag)
                }
            })
        }
    },

    mounted () {
        this.addTagsHeaderIcon()
        this.initSelected()
    },

    watch: {
        selectedTags (v) {
            const countAvailableOption = this.tags.length - Object.keys(this.headerActiveIcon).length + 1 // not count 'select all'
            this.headerActiveIcon.all = (v.length === countAvailableOption) ? 'mdi-checkbox-marked' : 'mdi-minus-box'

            this.$emit('input', v)
        }
    }
}
</script>

<style lang="scss" scoped>
.bg-white::before {
  background-color: white !important
}
</style>
